import WebSocketTerminal, {type InstanceInfo, type WebsocketTerminalEventHandler} from "./WebSocketTerminal.tsx";
import type {Dispatch, MutableRefObject, RefObject, SetStateAction} from "react";
import type {TerminalScreenContainerRef} from "./container/TerminalContainer.tsx";
import type {TerminalPasswordDialogProps, TerminalPasswordDialogRef} from "./TerminalPasswordDialog.tsx";
import type {TerminalStatusBarProps} from "./TerminalStatusBar.tsx";
import {swal_util} from "../../lib/SwalUtil.ts";
import {instance_manager, InstanceAPIConnectorError} from "../../lib/InstanceManager.ts";
import type {TerminalScreenEventHandlers, TerminalTab} from "./container/TerminalContainerContext.ts";
import type {TerminalShortCutKeyHelperHandler} from "./TerminalShortCutKeyHelper.tsx";
import type {NavigateFunction} from "react-router-dom";
import { t } from "i18next";

export class WebsocketTerminalEventHandlerImpl implements WebsocketTerminalEventHandler {
    private readonly navigate: NavigateFunction;
    private readonly instanceId: number;
    private readonly terminalScreenContainerRef: RefObject<TerminalScreenContainerRef>;
    private readonly terminalPasswordDialogRef: RefObject<TerminalPasswordDialogRef>;
    private readonly setInstanceInfo: Dispatch<SetStateAction<TerminalStatusBarProps | undefined>>;

    private instanceInfo: TerminalStatusBarProps = {
        status_color: undefined,

        instance_status: "",
        console_status: "",

        instance_name: "",
        os_image: "",
        instance_ip_list: []
    };
    private showWwalOpenPrompt = false;

    constructor(
        navigate: NavigateFunction,
        instanceId: number,
        terminalScreenContainerRef: RefObject<TerminalScreenContainerRef>,
        terminalPasswordDialogRef: RefObject<TerminalPasswordDialogRef>,
        setInstanceInfo: Dispatch<SetStateAction<TerminalStatusBarProps | undefined>>
    ) {
        this.navigate = navigate;
        this.instanceId = instanceId;

        this.terminalScreenContainerRef = terminalScreenContainerRef;
        this.terminalPasswordDialogRef = terminalPasswordDialogRef;

        this.setInstanceInfo = setInstanceInfo;
    }

    onInstanceInfo(detail: InstanceInfo): void {
        this.instanceInfo = {
            ...this.instanceInfo,
            console_status: t("terminal.status.connectingShort"), // "연결중"
            instance_ip_list: detail.addresses,
            instance_name: detail.name,
            instance_status: detail.status,
            os_image: detail.image_name,
            status_color: '#b87003'
        }

        this.setInstanceInfo(this.instanceInfo);

        (async () => {
            if (detail.status === "ACTIVE") {
                return
            }

            this.instanceInfo = {
                ...this.instanceInfo,
                status_color: '#ff0000'
            }

            this.setInstanceInfo(this.instanceInfo);

            if (detail.status === "SHUTOFF") {
                this.showWwalOpenPrompt = true;

                const confirm = await swal_util.show_question(
                    t("terminal.actions.boot.title"),                                // "부팅"
                    t("terminal.actions.boot.confirm")                                // "인스턴스가 꺼져있습니다...지금 부팅?"
                );
                if (confirm.isConfirmed) {
                    try {
                        await instance_manager.instanceAction.boot(this.instanceId);

                        setTimeout(() => {
                            let elapsedSeconds = 0;
                            const interval = setInterval(() => {
                                this.onChangeStatus(`Reloading in progress... ${elapsedSeconds++}/10`, "#b87003");
                            }, 1000);

                            setTimeout(() => {
                                clearInterval(interval);
                                this.navigate(0);
                            }, 1000);
                        }, 0);

                        return
                    } catch (err) {
                        if (err instanceof InstanceAPIConnectorError) {
                            await swal_util.show_error(`${err.localization}`);
                        } else {
                            await swal_util.show_error(t("common.requestFailed"));
                        }
                        console.error(err);
                        return;
                    }
                }
            }

            if (detail.status === "PAUSED") {
                this.showWwalOpenPrompt = true;


                const confirm = await swal_util.show_question(
                    t("terminal.actions.resume.title"),                               // "일시정지 해제"
                    t("terminal.actions.resume.confirm")                               // "일시정지 상태... 해제할까요?"
                );
                if (confirm.isConfirmed) {
                    try {
                        await instance_manager.instanceAction.resume(this.instanceId);
                        let elapsedSeconds = 0;
                        const interval = setInterval(() => {
                            this.onChangeStatus(`Reloading in progress... ${elapsedSeconds++}/50`, "#b87003");
                        }, 1000);

                        setTimeout(() => {
                            clearInterval(interval);
                            this.navigate(0);
                        }, 5000);
                    } catch (err) {
                        if (err instanceof InstanceAPIConnectorError) {
                            await swal_util.show_error(`${err.localization}`);
                        } else {
                            await swal_util.show_error(t("common.requestFailed"));
                        }
                        console.error(err);
                        return;
                    }
                }
            }

            if (!this.showWwalOpenPrompt) {
                this.onChangeStatus(t("terminal.waitingForActive"), "#b87003");     // "인스턴스 Active 상태 대기중"
            }
        })().finally()
    }


    onInit(): void {
        const terminalScreenContainerRef = this.terminalScreenContainerRef.current;
        if (!terminalScreenContainerRef) {
            console.error("terminalScreenContainerRef is null");
            return;
        }
        terminalScreenContainerRef.newTab();
    }


    onChangeStatus(console_status: string, statusColor?: string) {
        this.instanceInfo = {
            ...this.instanceInfo,
            console_status: console_status,
            status_color: statusColor ? statusColor : '#264EDD'
        }

        this.setInstanceInfo(this.instanceInfo);
    }

    onInitTab(reqKey: string, tabKey: number) {
        this.terminalScreenContainerRef.current!.bindTab(reqKey, tabKey);
        this.onChangeStatus(t("terminal.tabConnected"));                        // "탭 연결됨"
        this.setInstanceInfo(this.instanceInfo);
    }

    onFailedAuthentication(): void {
        this.terminalPasswordDialogRef.current?.show();
    }

    onIncorrectKeyPassword(): void {
        this.onChangeStatus(t("terminal.enterPassword"), "#ff0000");            // "비밀번호 입력"

        this.terminalPasswordDialogRef.current?.show();
    }

    onRecvData(tabKey: number, data: string): void {
        const terminalScreenContainerRef = this.terminalScreenContainerRef.current!;
        terminalScreenContainerRef.write(tabKey, data);
    }

    onChangeTitle(tabKey: number, title: string): void {
        const terminalScreenContainerRef = this.terminalScreenContainerRef.current!;
        terminalScreenContainerRef.setTitle(tabKey, title);
    }

    onClose(tabKey: number): void {
        const terminalScreenContainerRef = this.terminalScreenContainerRef.current!;
        terminalScreenContainerRef.removeTab(tabKey);
    }

    onTerminalNotPrepared(): void {
        if (this.showWwalOpenPrompt) {
            return;
        }

        this.onChangeStatus(t("terminal.waitingForSshdStart"), "#b87003");      // "sshd 시작 대기"
    }

    onErrMaxTerminalSessionExceeded(reqKey: string): void {
        const errMsg = t("terminal.errors.maxWebTabsExceeded");
        this.onChangeStatus(t("terminal.tabOpenIssue"), "#ff0000");             // "탭 Open 문제"

        swal_util.show_error(errMsg).finally(
            () => this.terminalScreenContainerRef.current?.removeTabByReqKey(reqKey)
        );
    }

    onErrSshdMaxSessionExceeded(reqKey: string): void {
        const errMsg = t("terminal.errors.sshdMaxSessionsExceeded");
        this.onChangeStatus(t("terminal.tabOpenIssue"), "#ff0000");

        swal_util.show_error(errMsg).finally(
            () => this.terminalScreenContainerRef.current?.removeTabByReqKey(reqKey)
        );
    }

    onInstanceNetworkErr(): void {
        const errMsg = t("terminal.errors.instanceCreatingRetryLater");
        this.onChangeStatus(t("terminal.warning"), "#ff0000");                  // "경고"
        swal_util.show_error(errMsg).finally(()=>window.close());
    }
}

export class TerminalContainerEventHandlerImpl implements TerminalScreenEventHandlers {
    private readonly webSocketTerminal: WebSocketTerminal;

    constructor(webSocketTerminal: WebSocketTerminal) {
        this.webSocketTerminal = webSocketTerminal;
    }

    onCloseTab(tabKey: number): void {
        this.webSocketTerminal.closeTab(tabKey);
    }

    onNewTab(newTab: TerminalTab): void {
        this.webSocketTerminal.newTab(newTab.reqKey);
    }

    onResize(cols: number, rows: number): void {
        this.webSocketTerminal.resize(cols, rows);
    }

    onWriteStdIn(tabKey: number, data: string): void {
        this.webSocketTerminal.send(tabKey, data);
    }

}

export class TerminalPasswordDialogEventHandlerImpl implements TerminalPasswordDialogProps {
    private readonly webSocketTerminal: WebSocketTerminal;

    constructor(webSocketTerminal: WebSocketTerminal) {
        this.webSocketTerminal = webSocketTerminal;
    }

    onEnterPassword(password: string) {
        this.webSocketTerminal.init(password)
    }

    onCancel() {
        window.close();
    }
}

export class TerminalShortCutKeyHelperHandlerImpl implements TerminalShortCutKeyHelperHandler {
    private readonly _setModalOpen: Dispatch<SetStateAction<boolean>>;
    private readonly _terminalScreenContainerRef: RefObject<TerminalScreenContainerRef>;


    constructor(
        setModalOpen: Dispatch<SetStateAction<boolean>>,
        terminalScreenContainerRef: RefObject<TerminalScreenContainerRef>
    ) {
        this._setModalOpen = setModalOpen;
        this._terminalScreenContainerRef = terminalScreenContainerRef;
    }

    onCreateTab(): void {
        this._terminalScreenContainerRef.current!.newTab();
    }

    onCloseTab(): void {
        this._terminalScreenContainerRef.current!.closeActiveTab();
    }

    onHelp(): void {
        this._setModalOpen(true);
    }

    onSelectLastTab(): void {
        this._terminalScreenContainerRef.current!.selectLastTab();
    }

    onSelectTab(index: number): void {
        this._terminalScreenContainerRef.current!.selectTab(index);
    }

    onLeftTab(): void {
        this._terminalScreenContainerRef.current!.selectLeftTab();
    }

    onRightTab(): void {
        this._terminalScreenContainerRef.current!.selectRightTab();
    }

    onCopy(): void {
        (async () => {
            const content = this._terminalScreenContainerRef.current!.getActiveTabSelection();
            if (content) {
                try {
                    await navigator.clipboard.writeText(content);
                } catch (error) {
                    console.error("Copy failed", error);
                }
            }
        })().finally();
    }

    onSearchDialog() {
        this._terminalScreenContainerRef.current!.showSearchDialog();
    }
}

export class ContextMenuEventHandler {
    private readonly _terminalScreenContainerRef: MutableRefObject<TerminalScreenContainerRef | null>;

    constructor(terminalScreenContainerRef: MutableRefObject<TerminalScreenContainerRef | null>) {
        this._terminalScreenContainerRef = terminalScreenContainerRef;
    }

    onClickCopy(): string | undefined {
        return this._terminalScreenContainerRef.current!.getActiveTabSelection();
    }

    onClickPaste(content: string | undefined) {
        return this._terminalScreenContainerRef.current!.pasteToActiveTab(content);
    }

    onClickNewTab() {
        this._terminalScreenContainerRef.current!.newTab();
    }

    onClickClose() {
        this._terminalScreenContainerRef.current!.closeActiveTab();
    }
}